import _ from 'lodash'
import axios from 'axios';

function composeGetter(parentKey, storeKeys, formatFn) {
  const result = {}

  for (let key of storeKeys) {
    result[`${parentKey}${_.upperFirst(key)}`] = state => {
      return formatPaginationData(
        _.get(state, `${parentKey}.${key}`, {}),
        parentKey,
        formatFn
      )
    }
  }

  return result
}

function formatPaginationData(data, parentKey, formatFn) {
  const totalPage = Math.ceil(data.total / data.limit)

  return {
    totalPage: totalPage || 1,
    total: data.total,
    [parentKey]: (data.data || []).map(item => {
      if (typeof formatFn === 'function') {
        return formatFn(item)
      }

      return item
    })
  }
}

function getAction(parentKey, endPoint, composeParams) {
  // Track in-flight requests per storeKey so a newer call can abort an older one.
  // Without this, fast clicks (e.g. paginating quickly) can let a stale response
  // overwrite the current page's data when it eventually lands.
  const inflight = {}

  async function fetchData({ commit, state }, params = {}) {

    if (!params.storeKey) {
      throw new Error('storeKey is required')
    }

    if (inflight[params.storeKey]) {
      inflight[params.storeKey].abort()
    }
    const controller = new AbortController()
    inflight[params.storeKey] = controller

    try {
      const data = await axios.get(endPoint, {
        params: composeParams(state, params),
        signal: controller.signal
      })

      commit(`set${_.upperFirst(parentKey)}`, {
        data: data.data,
        storeKey: params.storeKey
      })
    } catch (err) {
      if (axios.isCancel(err) || _.get(err, 'code') === 'ERR_CANCELED' || _.get(err, 'name') === 'CanceledError') {
        // Hang forever so callers' .finally() handlers don't flip loading
        // state off — only the latest (un-cancelled) request should do that
        // when its real response lands.
        return new Promise(() => {})
      }
      throw err
    } finally {
      if (inflight[params.storeKey] === controller) {
        delete inflight[params.storeKey]
      }
    }
  }

  return { [`fetch${_.upperFirst(parentKey)}`]: fetchData }
}

export default function generatePaginationDataStore(
  parentKey,
  storeKeys,
  endPoint,
  composeParams,
  formatFn
) {
  return {
    getter: composeGetter(parentKey, storeKeys, formatFn),
    state: { [parentKey]: {} },
    action: getAction(parentKey, endPoint, composeParams),
    mutation: {
      [`set${_.upperFirst(parentKey)}`]: function(state, data) {
        state[parentKey][data.storeKey] = data.data
        state[parentKey] = { ...state[parentKey] }
      }
    }
  }
}
